import React, { useState } from "react";
import {
  Drawer,
  List,
  Toolbar,
  Divider,
  useTheme,
  Box,
  useMediaQuery,
  Typography,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import NavItem from "./SidebarNavItem";

import {
  Home as HomeIcon,
  ViewKanban as KanbanIcon,
  Settings as SettingsIcon,
  Coffee as CoffeeIcon,
  Help as HelpIcon,
  ExpandLess,
  ExpandMore,
  RecentActors,
  QueueMusic as KaraokeIcon,
} from "@mui/icons-material";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { Wallet } from "lucide-react";
import { useAuth } from "../hooks";

const drawerWidth = 240;

const bottomNavSection = {
  items: [
    { text: "Settings", icon: <SettingsIcon />, path: "/settings" },
    // { text: "Help", icon: <HelpIcon />, path: "/help" },
  ],
};

type UserSidebarProps = {
  open: boolean;
  handleClose: () => void;
  coffeeCallChannel?: string;
  coffeeCallNotification?: any;
  toggleDrawer?: any;
  coffeeJoinChannel?: string | null;
};

const UserSidebar = ({
  open,
  coffeeCallChannel,
  coffeeCallNotification,
  toggleDrawer,
  coffeeJoinChannel,
  handleClose,
}: UserSidebarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const [bookingOpen, setBookingOpen] = useState(false);
  const handleBookingToggle = () => setBookingOpen(!bookingOpen);

  const { userInfo } = useAuth();
  const isGuestLite = userInfo?.profile_completed === false;
  const topNavSections = [
    {
      items: [
        { text: "TN", icon: <HomeIcon />, path: "/TNpage" },
        { text: "Stage cards", icon: <RecentActors />, path: "/stage-cards" },
        { text: "Karaoke", icon: <KaraokeIcon />, path: "/karaoke-card" },
      ],
    },
    {
      items: [
        { text: "Dashboard", icon: <KanbanIcon />, path: "/dashboard" },
        ...(!isGuestLite
          ? [{ text: "Wallet", icon: <Wallet />, path: "/wallet" }]
          : []),
      ],
    },
  ];

  /* Netflix-like tokens (design only) */
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    border: "rgba(255,255,255,0.12)",
    text: "rgba(255,255,255,0.92)",
    textMuted: "rgba(255,255,255,0.68)",
    red: "#E50914",
    redHover: "#b20710",
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={open}
      onClose={handleClose}
      sx={{
        width: open ? drawerWidth : 72,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: open ? drawerWidth : 72,
          transition: "width 240ms ease",
          background:
            "linear-gradient(180deg, rgba(10,10,10,0.98), rgba(10,10,10,0.92))",
          borderRight: `1px solid ${COLORS.border}`,
          overflowX: "hidden",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          color: COLORS.text,
          boxShadow:
            "inset 0 0 0 1px rgba(255,255,255,0.03), 2px 0 24px rgba(0,0,0,0.4)",
          backdropFilter: "blur(6px)",
          px: open ? 1 : 0,

          /* Global styles for all inner ListItemButton (works with NavItem) */
          "& .MuiListItemButton-root": {
            color: COLORS.textMuted,
            borderRadius: 8,
            mx: open ? 0.5 : 0.75,
            px: open ? 1.25 : 1,
            py: 1,
            justifyContent: open ? "flex-start" : "center",
            position: "relative",
            overflow: "hidden",
            transition: "background-color 160ms, color 160ms, transform 160ms",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.06)",
              color: COLORS.text,
            },
            "&:hover::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 8,
              bottom: 8,
              width: 2,
              borderRadius: 2,
              backgroundColor: COLORS.red,
              opacity: 0.65,
            },
          },
          "& .MuiListItemButton-root.Mui-selected": {
            background: "rgba(229,9,20,0.16)",
            color: COLORS.text,
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 8,
              bottom: 8,
              width: 3,
              borderRadius: 2,
              backgroundColor: COLORS.red,
            },
            "&:hover": {
              background: "rgba(229,9,20,0.22)",
            },
          },
          "& .MuiListItemIcon-root": {
            minWidth: 0,
            mr: open ? 1.25 : 0,
            color: COLORS.text,
            justifyContent: "center",
          },
          "& .MuiListItemText-root": {
            opacity: open ? 1 : 0,
            maxWidth: open ? "100%" : 0,
            transition: "opacity 160ms",
          },
          "& .MuiDivider-root": { borderColor: COLORS.border },
        },
      }}
    >
      <Box>
        {/* Header / Brand */}
        <Toolbar
          sx={{
            minHeight: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "flex-start" : "center",
          }}
        >
          {open ? (
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 900,
                  letterSpacing: "-0.02em",
                  color: COLORS.text,
                  "& span": { color: COLORS.red },
                }}
              >
                Stage<span>Link</span>
              </Typography>
              <sup
                style={{
                  fontSize: "0.6em",
                  color: COLORS.textMuted,
                  fontWeight: 700,
                }}
              >
                ™
              </sup>
            </Box>
          ) : (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: COLORS.red,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 18px rgba(229,9,20,0.35)",
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: "0.95rem",
                  fontFamily: "'Lexend', sans-serif",
                }}
              >
                SL
              </Typography>
            </Box>
          )}
        </Toolbar>

        {/* Join Coffee Call banner (dark, branded) */}
        <Box sx={{ display: "flex", flexDirection: "column", pt: 1 }}>
          {coffeeJoinChannel && (
            <Box
              sx={{
                background: "rgba(229,9,20,0.08)",
                borderRadius: 2,
                p: 1.25,
                mx: 1,
                border: `1px solid rgba(229,9,20,0.4)`,
              }}
            >
              <List
                key={coffeeJoinChannel || "default-key"}
                disablePadding
                sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
              >
                <NavItem
                  key={coffeeJoinChannel || "default-key"}
                  item={{
                    text: "Join coffee call",
                    icon: <CoffeeIcon />,
                    path: `/coffee-call/${coffeeJoinChannel}`,
                    fontSize: "0.95rem",
                  }}
                  open={open}
                  selected={true}
                  onClick={isMobile ? handleClose : undefined}
                />
              </List>
            </Box>
          )}

          {/* Section 1 */}
          <List
            disablePadding
            sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}
          >
            {topNavSections[0].items.map((item) => (
              <NavItem
                key={item.text}
                item={item}
                open={open}
                selected={location.pathname === item.path}
                onClick={isMobile ? handleClose : undefined}
              />
            ))}
          </List>

          {/* Collapsible “Sessions” */}
          <ListItemButton
            onClick={handleBookingToggle}
            sx={{
              borderRadius: 2,
              minHeight: 50,
              mx: 1,
              justifyContent: open ? "flex-start" : "center",
              px: open ? 1.25 : 1,
              py: 1,
              color: COLORS.textMuted,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.06)",
                color: COLORS.text,
                "& .MuiListItemIcon-root": { color: COLORS.text },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 1.25 : 0,
                justifyContent: "center",
                color: COLORS.text,
                transition: "color 0.2s ease",
                fontSize: "1.1rem",
              }}
            >
              <EventAvailableIcon />
            </ListItemIcon>
            {open && (
              <ListItemText
                primary="Sessions"
                sx={{
                  "& .MuiListItemText-primary": {
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    color: COLORS.text,
                  },
                }}
              />
            )}
            {open &&
              (bookingOpen ? (
                <ExpandLess sx={{ color: COLORS.text }} />
              ) : (
                <ExpandMore sx={{ color: COLORS.text }} />
              ))}
          </ListItemButton>

          <Collapse in={bookingOpen} timeout="auto" unmountOnExit>
            <List disablePadding sx={{ pl: open ? 4 : 1, gap: 0.25 }}>
              {/* <NavItem
                item={{
                  text: " Suits",
                  icon: <EventAvailableIcon />,
                  path: "/sessions",
                }}
                open={open}
                selected={location.pathname === "/sessions"}
                onClick={isMobile ? handleClose : undefined}
              /> */}
              <NavItem
                item={{
                  text: "Stage",
                  icon: <EventAvailableIcon />,
                  path: "/sessions/stagecard",
                }}
                open={open}
                selected={location.pathname === "/sessions/stagecard"}
                onClick={isMobile ? handleClose : undefined}
              />
              {/* <NavItem
                item={{
                  text: "Tickets",
                  icon: <EventAvailableIcon />,
                  path: "/tickets",
                }}
                open={open}
                selected={location.pathname === "/tickets"}
                onClick={isMobile ? handleClose : undefined}
              /> */}
            </List>
          </Collapse>

          <Divider
            sx={{
              width: open ? "82%" : "46%",
              mx: "auto",
              my: 3,
            }}
          />

          {/* Section 2 */}
          <List
            disablePadding
            sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}
          >
            {topNavSections[1].items.map((item) => (
              <NavItem
                key={item.text}
                item={item}
                open={open}
                selected={location.pathname === item.path}
                onClick={isMobile ? handleClose : undefined}
              />
            ))}
          </List>
        </Box>
      </Box>

      {/* Bottom section */}
      <Box sx={{ mb: 1.5 }}>
        <List
          disablePadding
          sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}
        >
          {bottomNavSection.items.map((item) => (
            <NavItem
              key={item.text}
              item={item}
              open={open}
              selected={location.pathname === item.path}
              onClick={isMobile ? handleClose : undefined}
            />
          ))}
        </List>
        <Box
          sx={{
            mt: 1,
            mx: open ? 1.25 : 0.75,
            height: 1,
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)",
            borderRadius: 1,
          }}
        />
      </Box>
    </Drawer>
  );
};

export default UserSidebar;
